import { supabase } from '@/lib/supabase';
import { FullReview, ReviewFormValues, ReviewStatus } from '@/types/review';
import { toast } from 'sonner';
import { updateReviewRequestStatus } from './review-requests';
import { calculateOverallRating } from './utils';
import { adminError } from '@/lib/utils/admin-logger';

// Create or update a review
export const saveReview = async (
  paperId: string,
  reviewerId: string,
  values: ReviewFormValues,
  status: ReviewStatus = 'draft',
  reviewId?: string,
  requestId?: string
): Promise<FullReview | null> => {
  try {
    const reviewData = {
      ...values,
      paper_id: paperId,
      reviewer_id: reviewerId,
      status,
      request_id: requestId,
      overall_rating: calculateOverallRating(values),
      ...(status === 'published' ? { published_at: new Date().toISOString() } : {})
    };

    let response;
    
    if (reviewId) {
      // Update existing review
      response = await supabase
        .from('reviews')
        .update(reviewData)
        .eq('id', reviewId)
        .select('*')
        .single();
    } else {
      // Create new review
      response = await supabase
        .from('reviews')
        .insert(reviewData)
        .select('*')
        .single();
    }
    
    const { data, error } = response;
    
    if (error) throw error;
    
    // Update the review request status if this is a completed review
    if (status === 'published' && requestId) {
      await updateReviewRequestStatus(requestId, 'completed');
    }
    
    return data;
  } catch (error) {
    adminError('Error saving review:', error);
    toast.error('Failed to save review');
    return null;
  }
};

// Publish a review (change status from draft to published)
export const publishReview = async (reviewId: string): Promise<boolean> => {
  try {
    // Get the current review first
    const { data: review, error: getError } = await supabase
      .from('reviews')
      .select('*')
      .eq('id', reviewId)
      .single();
    
    if (getError) throw getError;
    
    // Update the review status to published
    const { error: updateError } = await supabase
      .from('reviews')
      .update({
        status: 'published',
        published_at: new Date().toISOString(),
      })
      .eq('id', reviewId);
    
    if (updateError) throw updateError;
    
    // If the review is associated with a request, update the request status
    if (review && review.request_id) {
      await updateReviewRequestStatus(review.request_id, 'completed');
    }
    
    return true;
  } catch (error) {
    adminError('Error publishing review:', error);
    toast.error('Failed to publish review');
    return false;
  }
};

// Delete a review draft
export const deleteReviewDraft = async (reviewId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', reviewId)
      .eq('status', 'draft'); // Only allow deleting drafts
    
    if (error) throw error;
    return true;
  } catch (error) {
    adminError('Error deleting review draft:', error);
    return false;
  }
};
